import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';

import type {
	BulkCreateMappedFields,
	IssueCreateEmbedSuccessPayload,
} from '@atlassian/gic-anywhere';

import { fg } from '@confluence/feature-gating';

import { AiSubFooter } from '../ai-work-create/AiSubFooter';
import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from '../../providers/AiBulkNavigationContextProvider';
import { GicPanelBody } from '../work-create-common/GicPanelBody';
import {
	useBulkCreateContext,
	useGetIssueType,
	useSetNextIssueToEdit,
	useSetSingleIssuesCreated,
} from '../../providers/BulkCreateContextProvider';
import { saveCloudIdToLocalStorage } from '../../utils/localStorageHelpers';
import { useBulkCreateFlags } from '../../services/useBulkCreateFlags';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper';
import { getSource, useBulkCreateLoggingEvents } from '../../utils/useBulkCreateLoggingEvents';
import { messages } from '../../messages';
import {
	convertToGICFieldValue,
	mapGICPayloadToCreateIssueResponse,
} from '../../services/apiUtils';

import { BulkCreateNavigationHeader } from './BulkCreateNavigationHeader';

type BulkEditIssueViewProps = {
	isAiIssue?: boolean;
};

const bulkEditContentWrapper = xcss({
	flexGrow: '1',
});

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
export const BulkEditIssueView = ({ isAiIssue = false }: BulkEditIssueViewProps) => {
	const goToPage = useAiBulkNavigationGotoPage();
	const setSingleIssuesCreated = useSetSingleIssuesCreated();
	const setNextIssueToEdit = useSetNextIssueToEdit();
	const currentPage = useAiBulkNavigationCurrentPage();
	const { logSingleIssueCreateSuccess, logSingleIssueCreateFailure, logCreateSingleIssueClick } =
		useBulkCreateLoggingEvents();

	const [
		{
			currentIssueId,
			currentJiraProject,
			currentJiraSite,
			bulkIssuesList,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
		},
	] = useBulkCreateContext();
	const issueType = useGetIssueType();
	const currentIssue = bulkIssuesList.find((issue) => issue.id === currentIssueId);
	const { triggerSingleIssueCreatedFlag } = useBulkCreateFlags();

	const onDismiss = useCallback(() => {
		void goToPage('default');
	}, [goToPage]);

	const onClose = useCallback(
		(isCreateSuccess?: boolean) => {
			if (!isCreateSuccess) {
				void goToPage('default');
			}
		},
		[goToPage],
	);
	const isOpen = currentPage === 'edit_issue';

	const onFailure = useCallback(
		(error: Error | unknown) => {
			logSingleIssueCreateFailure(error);
		},
		[logSingleIssueCreateFailure],
	);

	const onSuccess = useCallback(
		(payload: IssueCreateEmbedSuccessPayload) => {
			logSingleIssueCreateSuccess();
			logCreateSingleIssueClick(payload?.issueId?.toString());
			triggerSingleIssueCreatedFlag(payload);

			// Save the cloud id in localStorage if an issue is created
			saveCloudIdToLocalStorage(payload);

			const response = mapGICPayloadToCreateIssueResponse(payload);
			const issueCreated = {
				...response,
				summary: response.summary,
				rowIndex: currentIssue?.rowIndex,
			};
			void setSingleIssuesCreated(issueCreated);

			void setNextIssueToEdit();
		},
		[
			triggerSingleIssueCreatedFlag,
			logSingleIssueCreateSuccess,
			setSingleIssuesCreated,
			setNextIssueToEdit,
			logCreateSingleIssueClick,
			currentIssue,
		],
	);

	if (!currentIssue || !currentJiraProject || !issueType || !currentJiraSite) {
		// TODO return error screen component
		return null;
	}
	const { summary, description, id: issueId, assignee, labels, parent } = currentIssue;

	const additionalMappedFields: BulkCreateMappedFields = {
		...(labels?.length && { labels }),
		...(parent?.id && { parent: parent.id }),
		...(currentIssue.additionalFields?.reduce(
			(acc, field) => {
				acc[field.field.fieldId] = convertToGICFieldValue(field);
				return acc;
			},
			{} as Record<string, any>,
		) ?? {}),
	};

	return (
		<SidePanelWrapper isOpen={isOpen}>
			<Stack testId="bulk-edit-content-wrapper" xcss={bulkEditContentWrapper}>
				<Box padding="space.200">
					<BulkCreateNavigationHeader onClose={onDismiss}>
						{fg('confluence-issue-terminology-refresh') ? (
							<FormattedMessage {...messages.bulkCreateEditHeaderTextIssueTermRefresh} />
						) : (
							<FormattedMessage {...messages.bulkCreateEditHeaderText} />
						)}
					</BulkCreateNavigationHeader>
				</Box>
				<GicPanelBody
					key={`issue-id-${issueId}`}
					cloudId={currentJiraSite.cloudId}
					onSuccess={onSuccess}
					onFailure={onFailure}
					onClose={onClose}
					analyticsSource={getSource(isCreatedFromTable, isCreatedFromActionItems)}
					summary={summary}
					descriptionAdf={description}
					issueTypeId={issueType.id}
					configuration={{ disableProjectDropdown: true, displaySitePicker: false }}
					projectId={currentJiraProject.id}
					assigneeId={assignee}
					isCreatedWithAi={isCreatedWithAi}
					isCreatedFromTable={isCreatedFromTable}
					{...(Object.keys(additionalMappedFields).length > 0 && {
						additionalMappedFields: {
							...additionalMappedFields,
							pid: currentJiraProject.id,
							issuetype: issueType.id,
							...(description && { description }),
						},
					})}
				/>
				{isAiIssue && <AiSubFooter />}
			</Stack>
		</SidePanelWrapper>
	);
};
